import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { getRenderBufferHeight } from '../scene-manager.js';

// Shared-rig contract (see the framing addendum). 'forbidden': a discharge is
// its own light source and the brightest thing on screen by a wide margin. A
// rig underneath it would only add a second, weaker, contradictory one.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'lichtenberg',
  family: 'vector',
  danceability: 0.60,
  energyLevel: 0.88,
  bpmMin: 110,
  bpmMax: 185,
  movement: 0.65,
  tonality: 0.45,
  prefers: { lowEnd: [0.45, 1.0], dynamics: [0.10, 0.75] },
  enabled: true
};

// Lichtenberg: electrical discharges that branch by dielectric breakdown,
// striking on the track's drops. Techno, drum'n'bass, dubstep, hard electro.
//
// THE MODEL, and the one thing that makes this file worth writing: Niemeyer,
// Pietronero and Wiesmann's DBM. The structure grows one site at a time, and
// the probability of a free neighbour being the next site is proportional to
// the local potential raised to a power eta. That is the whole difference
// between a real Lichtenberg figure and a fractal tree: the branch follows the
// FIELD, not a random number. Every visual property people recognise - the few
// dominant trunks, the short secondary ramifications, the way branches avoid
// each other because each one screens the field behind it - falls out of that
// one rule and cannot be faked by recursion with jitter.
//
// eta is the only parameter that matters and it changes everything:
//   1.0  dense, bushy, nearly isotropic
//   2.0  lightning: a few main branches with short secondaries
//   4.0  nearly straight and angular - a fracture rather than a bolt
//
// SOLVING LAPLACE WITHOUT KILLING THE CPU. Re-solving the field after every
// added site would be 256x256 relaxations x 900 sites, which is not payable.
// But adding a site only perturbs its own neighbourhood: the potential far away
// barely moves. So relaxation is INCREMENTAL - 15 Gauss-Seidel iterations
// within 12 cells of the new site, and nothing else. That is about 7000
// operations per site instead of 65000, and the error outside the disc is far
// below what a 5-pixel-wide glowing line could show.
//
// And the whole figure is built in ONE deferred task, then replayed. Growth
// never happens inside a render frame.

const GRID = 256;

// Incremental relaxation - see the header. These two numbers are the entire
// reason this is affordable.
const RELAX_RADIUS = 12;
const RELAX_ITERS = 15;

// The initial solve, once per discharge, before any site is added. Sliced like
// everything else in the build.
const SEED_SOLVE_ITERS = 70;

// --- discharge types -------------------------------------------------------
// The type is chosen by how hard the drop is: the hardest get FRACTURE, the
// middling ones BOLT, the weakest BLOOM.
const TYPES = {
  bolt: { eta: 2.0, sites: 900, bias: 0.55, origin: 'top' },
  bloom: { eta: 1.4, sites: 1200, bias: 0.0, origin: 'centre' },
  fracture: { eta: 3.5, sites: 500, bias: 0.25, origin: 'edge' }
};

// --- animation -------------------------------------------------------------
// A discharge propagates; it does not appear. Every site carries its RANK in
// the growth order and the reveal walks that order.
const GROW_MS_MIN = 90;
const GROW_MS_MAX = 180;
const HOLD_MS = 70;
const FADE_MS_MIN = 350;
const FADE_MS_MAX = 700;
// Where a branch of a given depth starts to go out, as a fraction of the fade.
// The tips leave at 0 and the trunk not until 0.55, which is what produces the
// characteristic lingering spine rather than a uniform dissolve.
const FADE_START_TRUNK = 0.55;
const FADE_SPAN = 0.45;

// --- rendering -------------------------------------------------------------
// Widths are in buildRenderPaths (7 px trunk, 1.5 px tips). Around each
// stroke: a pure white core 30% of its thickness, a blue-violet halo four
// times its thickness.
const CORE_SHARE = 0.30;
const HALO_SPAN = 4.0;
const PEAK_LUMINANCE = 0.98;   // it has to DAZZLE
// A pink bolt does not read as a bolt. The palette gets a say, but it is
// pulled 45% of the way to blue-violet whatever it was.
const BLUE_VIOLET = 0x6a5cff;
const BLUE_PULL = 0.45;

// The three previous discharges, drawn with the same pipeline, blue-violet,
// no core, at these levels, gone in 3 s.
const AFTERGLOW_ALPHA = [0.15, 0.08, 0.04];
const RESIDUAL_MS = 3000;

// --- the sky -----------------------------------------------------------------
const SKY_FLASH = 0.35;          // the whole sky, for FLASH_FRAMES frames
const FLASH_FRAMES = 2;
const CLOUD_LIGHT_MS = 400;      // the clouds lit from inside, then out
const HORIZON_Y = -0.80;         // the silhouette's mean top, in height units
const BLOOM_SPAN = 0.45;         // of the frame's width

// --- between strikes: the storm keeps working on the beat ---------------------
// A 0.60 visualizer cannot sit still for the four to eight seconds between two
// strikes. What a storm does meanwhile is light its clouds from INSIDE
// (intra-cloud lightning, no channel visible) and throw small bolts on the far
// horizon. Both are glows or distant strokes: the brief's "no flying lines"
// still holds.
const PULSE_SLOTS = 6;
const PULSE_MS = 240;            // one intra-cloud flash, out in about a quarter second
const PULSE_LEVEL = [0.40, 0.75];
const PULSE_DOWNBEAT = 1.35;     // the bar's first beat hits harder
const PULSE_CHANCE = 0.9;        // on the other beats
const QUIET_AFTER_STRIKE_MS = 1200;
const DISTANT_EVERY_BARS = 2;
const DISTANT_CHANCE = 0.7;
const DISTANT_MS = 280;
const DISTANT_LEVEL = 0.55;
const DISTANT_POOL = 6;
const DISTANT_SLOTS = 3;
const DISTANT_SITES = 110;
const FALLBACK_BPM = 120;        // no tempo at all: a beat of our own
const CLOUD_DRIFT = 0.007;       // texture widths per second

// --- pre-drop ionisation -----------------------------------------------------
const FILAMENT_GRID = 40;
const FILAMENT_SITES = [20, 40];
const FILAMENT_LIFE_MS = 150;
const FILAMENT_COUNT = [5, 10];
const FILAMENT_POOL = 16;
const FILAMENT_SLOTS = 10;

// --- scheduling ------------------------------------------------------------
const MIN_GAP_MS = 4000;       // without analysis: at most one every 4s
const ANTICIPATE_BEATS = 4;
const LOW_PEAK_RISE = 0.12;

const TIER = {
  // Tier 0 keeps one memory, not three: each is a full stroke mesh.
  0: { retain: 1, siteScale: 0.39, airGlow: false, dust: false },
  1: { retain: 2, siteScale: 0.67, airGlow: true, dust: false },
  2: { retain: 3, siteScale: 1.0, airGlow: true, dust: true }
};
const DEFAULT_TIER = 2;

const SAFE_FLOOR = 0.45;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(n) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(n);
}

// ---------------------------------------------------------------------------
// The DBM builder. Pure CPU, sliced, and completely independent of rendering -
// it never touches THREE and never runs inside a frame.
// ---------------------------------------------------------------------------
class DischargeBuilder {
  // safeRect: {cx, cy, hx, hy} in grid coordinates - the impact point is
  // forbidden from landing inside it (a bolt terminating behind the cover
  // would be invisible and would read as a bolt that stops in mid-air).
  // n: the grid's side - GRID for a discharge, much less for a pre-drop
  // filament. centred: seed a radial figure in the middle rather than at random.
  constructor(type, eta, targetSites, safeRect, n, centred) {
    const N = n || GRID;
    this.centred = !!centred;
    this.N = N;
    this.type = type;
    this.eta = eta;
    this.target = targetSites;
    this.safeRect = safeRect;

    this.pot = new Float32Array(N * N);
    this.solid = new Uint8Array(N * N);
    this.parent = new Int32Array(N * N).fill(-1);
    this.depth = new Uint8Array(N * N);
    this.rank = new Int32Array(N * N).fill(-1);
    this.childCount = new Uint8Array(N * N);

    this.cand = [];               // candidate site indices
    this.candW = [];              // their weights, pow(potential, eta)
    this.isCand = new Uint8Array(N * N);

    this.sites = [];              // growth order
    this.done = false;
    this._seedIters = 0;

    // Which side the discharge is heading FOR. Decided before the field is
    // built, because the field is built around it.
    if (type === 'bloom') { this.electrode = 'radial'; this.seedSide = null; }
    else if (type === 'bolt') { this.electrode = 'bottom'; this.seedSide = 'top'; }
    else {
      const sides = ['left', 'right', 'top', 'bottom'];
      this.seedSide = sides[(Math.random() * 4) | 0];
      this.electrode = { left: 'right', right: 'left', top: 'bottom', bottom: 'top' }[this.seedSide];
    }

    this._initField();
    this._seedStructure();
  }

  idx(x, y) { return y * this.N + x; }

  // BOUNDARY CONDITIONS, and getting these wrong is the difference between a
  // Lichtenberg figure and nothing at all.
  //
  // The first version put potential 1 on all four borders, as the brief says.
  // That is right for the radial figure, whose seed sits in the middle - but
  // for a bolt or a fracture, whose seed sits ON an edge, it means the seed is
  // born immediately adjacent to the maximum of the field. The discharge then
  // does exactly what the model says it should: it races to the nearest
  // high-potential cells and crawls ALONG the border. Measured, the whole
  // 500-site figure lay between y = -0.984 and -0.992 - a one-pixel line on
  // the bottom edge of the frame, geometrically correct and completely
  // invisible.
  //
  // So the electrodes are per type. The seed sits on the grounded side and the
  // attracting electrode is the far one, which is what a discharge actually
  // spans: two electrodes with a gap between them, not a box.
  _initField() {
    const N = this.N, p = this.pot;
    const e = this.electrode;
    for (let y = 0; y < N; y++) {
      for (let x = 0; x < N; x++) {
        let v;
        if (e === 'radial') {
          const dx = x / (N - 1) - 0.5, dy = y / (N - 1) - 0.5;
          v = Math.min(1, 2 * Math.sqrt(dx * dx + dy * dy));
        } else if (e === 'bottom') v = y / (N - 1);
        else if (e === 'top') v = 1 - y / (N - 1);
        else if (e === 'right') v = x / (N - 1);
        else v = 1 - x / (N - 1);
        p[y * N + x] = v;
      }
    }
    this._applyBorder();
  }

  _applyBorder() {
    const N = this.N, p = this.pot, e = this.electrode;
    const row = (y, v) => { for (let x = 0; x < N; x++) p[y * N + x] = v; };
    const col = (x, v) => { for (let y = 0; y < N; y++) p[y * N + x] = v; };
    if (e === 'radial') {
      row(0, 1); row(N - 1, 1); col(0, 1); col(N - 1, 1);
      return;
    }
    // A ramp down the two flanking sides, so the field between the electrodes
    // is uniform rather than pinched toward the corners.
    const ramp = (x, up) => {
      for (let y = 0; y < N; y++) p[y * N + x] = up ? y / (N - 1) : 1 - y / (N - 1);
    };
    const rampH = (y, right) => {
      for (let x = 0; x < N; x++) p[y * N + x] = right ? x / (N - 1) : 1 - x / (N - 1);
    };
    if (e === 'bottom') { row(N - 1, 1); row(0, 0); ramp(0, true); ramp(N - 1, true); }
    else if (e === 'top') { row(0, 1); row(N - 1, 0); ramp(0, false); ramp(N - 1, false); }
    else if (e === 'right') { col(N - 1, 1); col(0, 0); rampH(0, true); rampH(N - 1, true); }
    else { col(0, 1); col(N - 1, 0); rampH(0, false); rampH(N - 1, false); }
  }

  _seedStructure() {
    const N = this.N;
    let sx, sy;
    if (this.type === 'bolt') {
      sx = (N * 0.25 + Math.random() * N * 0.5) | 0;
      sy = 2;
    } else if (this.type === 'bloom' && this.centred) {
      sx = N >> 1; sy = N >> 1;
    } else if (this.type === 'bloom') {
      // Kept clear of the safe rectangle: the classic figure radiates from its
      // origin, so an origin behind the cover would hide the whole centre.
      let tries = 0;
      do {
        sx = (N * 0.2 + Math.random() * N * 0.6) | 0;
        sy = (N * 0.2 + Math.random() * N * 0.6) | 0;
        tries++;
      } while (tries < 40 && this._inSafe(sx, sy));
    } else {
      const t = (N * 0.25 + Math.random() * N * 0.5) | 0;
      if (this.seedSide === 'left') { sx = 2; sy = t; }
      else if (this.seedSide === 'right') { sx = N - 3; sy = t; }
      else if (this.seedSide === 'top') { sx = t; sy = 2; }
      else { sx = t; sy = N - 3; }
    }
    const s = this.idx(sx, sy);
    this._addSite(s, -1, 0, false);
  }

  _inSafe(x, y) {
    const r = this.safeRect;
    if (!r) return false;
    return Math.abs(x - r.cx) <= r.hx && Math.abs(y - r.cy) <= r.hy;
  }

  _addSite(s, parentIdx, depth, relax) {
    this.solid[s] = 1;
    this.pot[s] = 0;
    this.parent[s] = parentIdx;
    this.depth[s] = depth;
    this.rank[s] = this.sites.length;
    this.sites.push(s);
    if (this.isCand[s]) {
      this.isCand[s] = 0;
      const i = this.cand.indexOf(s);
      if (i >= 0) { this.cand.splice(i, 1); this.candW.splice(i, 1); }
    }
    this._addNeighbourCandidates(s);
    if (relax) this._relaxAround(s);
  }

  _addNeighbourCandidates(s) {
    const N = this.N, x = s % N, y = (s / N) | 0;
    const nb = [[1, 0], [-1, 0], [0, 1], [0, -1]];
    for (const d of nb) {
      const nx = x + d[0], ny = y + d[1];
      if (nx <= 0 || ny <= 0 || nx >= N - 1 || ny >= N - 1) continue;
      const ni = ny * N + nx;
      if (this.solid[ni] || this.isCand[ni]) continue;
      this.isCand[ni] = 1;
      this.cand.push(ni);
      this.candW.push(this._weight(ni));
    }
  }

  // The DBM rule itself. The directional bias for a bolt is added to the
  // POTENTIAL before the exponent, not multiplied onto the result afterwards -
  // it has to be a bias in the field the branch is following, otherwise it is
  // just a preference layered on top of an unbiased walk.
  _weight(i) {
    const N = this.N;
    let v = this.pot[i];
    const bias = TYPES[this.type].bias;
    if (bias > 0) {
      const y = (i / N) | 0;
      if (this.type === 'bolt') v += bias * (y / N);
      else {
        const x = i % N;
        const dx = (x / N - 0.5), dy = (y / N - 0.5);
        v += bias * (1.0 - Math.min(1, 2 * Math.sqrt(dx * dx + dy * dy)));
      }
    }
    if (v <= 0) return 0;
    // The safe rectangle DISCOURAGES the discharge without blocking it.
    // Filtering only the origin cannot keep a strike out from behind the cover
    // - a bolt's impact point is emergent - but at 0.15 the penalty was so
    // strong the bolt simply stopped dead at the cover's top edge, and the
    // brief is explicit that discharges cross the frame and pass BEHIND it.
    // 0.6 biases the branch around the cover while leaving it able to cross,
    // and since a figure only terminates on reaching the electrode, the impact
    // still lands on the far edge rather than in the middle of the picture.
    if (this._inSafe(i % N, (i / N) | 0)) v *= 0.6;
    return Math.pow(v, this.eta);
  }

  _reachedElectrode(i) {
    const N = this.N, x = i % N, y = (i / N) | 0, m = 3;
    const e = this.electrode;
    if (e === 'radial') return x <= m || y <= m || x >= N - 1 - m || y >= N - 1 - m;
    if (e === 'bottom') return y >= N - 1 - m;
    if (e === 'top') return y <= m;
    if (e === 'right') return x >= N - 1 - m;
    return x <= m;
  }

  // Gauss-Seidel, but only inside a disc around the site that just changed.
  _relaxAround(s) {
    const N = this.N, p = this.pot, sol = this.solid;
    const cx = s % N, cy = (s / N) | 0;
    const x0 = Math.max(1, cx - RELAX_RADIUS), x1 = Math.min(N - 2, cx + RELAX_RADIUS);
    const y0 = Math.max(1, cy - RELAX_RADIUS), y1 = Math.min(N - 2, cy + RELAX_RADIUS);
    const r2 = RELAX_RADIUS * RELAX_RADIUS;
    for (let it = 0; it < RELAX_ITERS; it++) {
      for (let y = y0; y <= y1; y++) {
        const dy = y - cy;
        for (let x = x0; x <= x1; x++) {
          const dx = x - cx;
          if (dx * dx + dy * dy > r2) continue;
          const i = y * N + x;
          if (sol[i]) continue;
          p[i] = 0.25 * (p[i - 1] + p[i + 1] + p[i - N] + p[i + N]);
        }
      }
    }
    // Candidates inside the disc had their potential change, so their weights
    // are stale. Only those - this is the incremental half of the saving.
    for (let k = 0; k < this.cand.length; k++) {
      const i = this.cand[k];
      const x = i % N, y = (i / N) | 0;
      const dx = x - cx, dy = y - cy;
      if (dx * dx + dy * dy <= r2) this.candW[k] = this._weight(i);
    }
  }

  // The one-time initial solve, sliced so it never blocks.
  _solveSlice(iters) {
    const N = this.N, p = this.pot, sol = this.solid;
    for (let it = 0; it < iters; it++) {
      for (let y = 1; y < N - 1; y++) {
        for (let x = 1; x < N - 1; x++) {
          const i = y * N + x;
          if (sol[i]) continue;
          p[i] = 0.25 * (p[i - 1] + p[i + 1] + p[i - N] + p[i + N]);
        }
      }
    }
    this._seedIters += iters;
    if (this._seedIters >= SEED_SOLVE_ITERS) {
      for (let k = 0; k < this.cand.length; k++) this.candW[k] = this._weight(this.cand[k]);
      return true;
    }
    return false;
  }

  _grow() {
    let total = 0;
    for (let k = 0; k < this.candW.length; k++) total += this.candW[k];
    if (total <= 0 || !this.cand.length) { this.done = true; return; }
    let r = Math.random() * total;
    let pick = this.cand.length - 1;
    for (let k = 0; k < this.candW.length; k++) {
      r -= this.candW[k];
      if (r <= 0) { pick = k; break; }
    }
    const s = this.cand[pick];

    // The parent is the adjacent solid site - whichever of them is closest to
    // the origin in growth order, so the tree stays a tree.
    const N = this.N, x = s % N, y = (s / N) | 0;
    let best = -1, bestRank = Infinity;
    const nb = [[1, 0], [-1, 0], [0, 1], [0, -1]];
    for (const d of nb) {
      const ni = (y + d[1]) * N + (x + d[0]);
      if (!this.solid[ni]) continue;
      if (this.rank[ni] < bestRank) { bestRank = this.rank[ni]; best = ni; }
    }
    if (best < 0) { this.done = true; return; }

    // Branch depth: a site continues its parent's branch if it is the parent's
    // FIRST child, and starts a new, thinner one otherwise. Walking back to the
    // origin is therefore unnecessary - the depth is carried forward.
    const isBranch = this.childCount[best] > 0;
    this.childCount[best]++;
    this._addSite(s, best, Math.min(255, this.depth[best] + (isBranch ? 1 : 0)), true);

    // A discharge ENDS when it bridges the gap. Without this the structure
    // reaches the electrode and then grows ALONG it, because every cell of
    // that border is at the maximum potential - at eta 4 it came out as two
    // perfectly straight runs down the top and right edges, which reads as a
    // circuit trace rather than a discharge. The site target is therefore a
    // cap, not a goal: a figure that bridges early is simply a short one.
    if (this._reachedElectrode(s)) this.done = true;
    if (this.sites.length >= this.target) this.done = true;
  }

  // Returns true when the whole figure is finished.
  step(budget) {
    if (this.done) return true;
    if (this._seedIters < SEED_SOLVE_ITERS) {
      this._solveSlice(Math.max(4, Math.min(12, budget)));
      return false;
    }
    for (let i = 0; i < budget && !this.done; i++) this._grow();
    return this.done;
  }

  // The finished tree, for the rendering pipeline below: parents, growth
  // ranks, and how many tips each site feeds. `impact` is the site that
  // bridged the gap (the last one, when the figure ended on the electrode).
  tree() {
    const N = this.N, sites = this.sites;
    const leaves = new Int32Array(N * N);
    // Children always rank after their parent, so one pass in reverse order
    // settles every subtree before its root is read.
    for (let k = sites.length - 1; k >= 0; k--) {
      const s = sites[k];
      if (leaves[s] === 0) leaves[s] = 1;
      const pa = this.parent[s];
      if (pa >= 0) leaves[pa] += leaves[s];
    }
    const last = sites.length ? sites[sites.length - 1] : -1;
    const impact = last >= 0 && this._reachedElectrode(last) ? last : -1;
    return { N, sites, parent: this.parent, rank: this.rank, leaves, impact };
  }
}

// ---------------------------------------------------------------------------
// THE RENDERING PIPELINE. The DBM is right; what the first version drew was
// its raw skeleton, and on a 4-connected grid that is a staircase: every
// segment horizontal or vertical. So the tree is re-drawn, not re-grown:
//   1. every site moves by a fixed random offset inside its cell (JITTER),
//   2. the tree is cut into PATHS, trunk to tip, stopping at every fork,
//   3. each path keeps one point in three and goes through a Catmull-Rom,
//   4. each smoothed segment gets two levels of perpendicular midpoint
//      displacement at 12% of its length - the nervous, angular line of a
//      real bolt, with no grid left in it,
//   5. width follows the square root of the number of tips a segment feeds.
// map(gx, gy) -> [x, y] in height units places the grid in the frame.
// ---------------------------------------------------------------------------
const JITTER = 0.45;             // of a cell
const KEEP_EVERY = 3;
const CR_SAMPLES = 2;            // Catmull-Rom samples per span
const MIDPOINT_AMP = 0.12;       // of the segment's length
const MIDPOINT_LEVELS = 2;
const WIDTH_TRUNK_PX = 7.0;
const WIDTH_TIP_PX = 1.5;
const MIN_TIP_SITES = 3;         // tip branches this short are not drawn

// A fixed offset per site, from its index: the same site always sits at the
// same place, whichever figure it belongs to.
function siteJitter(i, axis) {
  let h = (i * 374761393 + axis * 668265263) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return ((h >>> 0) / 4294967296 - 0.5) * 2 * JITTER;
}

// Centripetal Catmull-Rom (Barry-Goldman). Centripetal, not uniform: after
// the jitter and the subsampling the spacing is uneven, and a uniform spline
// overshoots into little loops on a short span.
function catmullRom(P0, P1, P2, P3, t) {
  const d = (a, b) => Math.max(1e-6, Math.sqrt(Math.hypot(b[0] - a[0], b[1] - a[1])));
  const t0 = 0, t1 = t0 + d(P0, P1), t2 = t1 + d(P1, P2), t3 = t2 + d(P2, P3);
  const u = t1 + (t2 - t1) * t;
  const L = (A, B, ta, tb) => {
    const w = (u - ta) / (tb - ta);
    return [A[0] + (B[0] - A[0]) * w, A[1] + (B[1] - A[1]) * w, A[2] + (B[2] - A[2]) * w, A[3] + (B[3] - A[3]) * w];
  };
  const A1 = L(P0, P1, t0, t1), A2 = L(P1, P2, t1, t2), A3 = L(P2, P3, t2, t3);
  const B1 = L(A1, A2, t0, t2), B2 = L(A2, A3, t1, t3);
  return L(B1, B2, t1, t2);
}

// Points are [x, y, rank, width].
function midpointDisplace(pts, levels, amp, rnd) {
  let cur = pts;
  for (let l = 0; l < levels; l++) {
    const out = [cur[0]];
    for (let i = 1; i < cur.length; i++) {
      const a = cur[i - 1], b = cur[i];
      const dx = b[0] - a[0], dy = b[1] - a[1];
      const len = Math.hypot(dx, dy);
      const o = (rnd() * 2 - 1) * amp * len;
      const nx = len > 0 ? -dy / len : 0, ny = len > 0 ? dx / len : 0;
      out.push([(a[0] + b[0]) / 2 + nx * o, (a[1] + b[1]) / 2 + ny * o,
                (a[2] + b[2]) / 2, (a[3] + b[3]) / 2]);
      out.push(b);
    }
    cur = out;
  }
  return cur;
}

function buildRenderPaths(tree, map, opts) {
  const { N, sites, parent, rank, leaves, impact } = tree;
  const rnd = (opts && opts.rnd) || Math.random;
  const minTip = opts && opts.minTip != null ? opts.minTip : MIN_TIP_SITES;
  const out = { count: 0, cx: [], cy: [], tx: [], ty: [], rank: [], width: [],
                pathStart: [], pathLen: [], total: sites.length, originX: 0, originY: 0,
                impactX: null, impactY: null };
  if (sites.length < 2) return out;

  const children = new Map();
  for (let k = 1; k < sites.length; k++) {
    const s = sites[k], pa = parent[s];
    if (pa < 0) continue;
    if (!children.has(pa)) children.set(pa, []);
    children.get(pa).push(s);
  }
  // The channel that bridged the gap is the TRUNK, whatever it feeds: the
  // return stroke runs along it, and a bolt whose path to the ground thins to
  // a tip on the way down does not read as a strike.
  const nMax = leaves[sites[0]];
  const trunk = new Uint8Array(N * N);
  if (impact >= 0) for (let s = impact; s >= 0; s = parent[s]) trunk[s] = 1;
  const sq = Math.sqrt(Math.max(1, nMax));
  const widthOf = (s) => {
    const n = trunk[s] ? nMax : leaves[s];
    return sq > 1 ? WIDTH_TIP_PX + (WIDTH_TRUNK_PX - WIDTH_TIP_PX) * (Math.sqrt(n) - 1) / (sq - 1) : WIDTH_TRUNK_PX;
  };
  const pos = (s) => map((s % N) + siteJitter(s, 0), ((s / N) | 0) + siteJitter(s, 1));

  // Paths: from the root, and from every fork, down each child until the next
  // fork or a tip. A branch starts AT its fork, so the join is closed.
  const paths = [];
  const stack = [[sites[0], sites[0]]];
  while (stack.length) {
    const [from, first] = stack.pop();
    const path = from === first ? [first] : [from, first];
    let cur = first;
    for (;;) {
      const ch = children.get(cur);
      if (!ch || ch.length === 0) break;
      if (ch.length > 1) { for (const c of ch) stack.push([cur, c]); break; }
      cur = ch[0];
      path.push(cur);
    }
    // A tip branch of one to three sites leaves its parent at a right angle
    // (the grid is 4-connected) and is too short for the spline to bend: drawn,
    // it is a comb tooth, and a trunk lined with them reads as a grid again.
    // The DBM keeps them - they screen the field - but they are not drawn.
    const stub = from !== first && !(children.get(cur) || []).length && path.length - 1 <= minTip;
    if (path.length >= 2 && !stub) paths.push(path);
  }

  for (const path of paths) {
    const n = path.length;
    // The fork point takes its CHILD's rank and width, so a branch appears
    // when its first real site does, and starts thin.
    const P = path.map((s, i) => {
      const src = i === 0 && n > 1 && path[0] !== sites[0] ? path[1] : s;
      const p = pos(s);
      return [p[0], p[1], rank[src], widthOf(src)];
    });
    const ctrl = [];
    for (let i = 0; i < n; i += KEEP_EVERY) ctrl.push(P[i]);
    if ((n - 1) % KEEP_EVERY !== 0) ctrl.push(P[n - 1]);
    let smooth;
    if (ctrl.length === 2) smooth = ctrl;
    else {
      smooth = [];
      for (let i = 0; i < ctrl.length - 1; i++) {
        const p0 = ctrl[Math.max(0, i - 1)], p1 = ctrl[i], p2 = ctrl[i + 1], p3 = ctrl[Math.min(ctrl.length - 1, i + 2)];
        // Ends: reflect, so the spline leaves the end point along the path.
        const e0 = i === 0 ? [2 * p1[0] - p2[0], 2 * p1[1] - p2[1], p1[2], p1[3]] : p0;
        const e3 = i + 2 >= ctrl.length ? [2 * p2[0] - p1[0], 2 * p2[1] - p1[1], p2[2], p2[3]] : p3;
        for (let k = 0; k < CR_SAMPLES; k++) smooth.push(catmullRom(e0, p1, p2, e3, k / CR_SAMPLES));
      }
      smooth.push(ctrl[ctrl.length - 1]);
    }
    const pts = midpointDisplace(smooth, MIDPOINT_LEVELS, MIDPOINT_AMP, rnd);
    out.pathStart.push(out.count);
    out.pathLen.push(pts.length);
    for (let i = 0; i < pts.length; i++) {
      const a = pts[Math.max(0, i - 1)], b = pts[Math.min(pts.length - 1, i + 1)];
      out.cx.push(pts[i][0]); out.cy.push(pts[i][1]);
      out.tx.push(b[0] - a[0]); out.ty.push(b[1] - a[1]);
      out.rank.push(pts[i][2]); out.width.push(pts[i][3]);
      out.count++;
    }
  }
  const o = pos(sites[0]);
  out.originX = o[0]; out.originY = o[1];
  if (impact >= 0) { const q = pos(impact); out.impactX = q[0]; out.impactY = q[1]; }
  return out;
}

// ---------------------------------------------------------------------------

const boltVertexShader = `
  precision highp float;

  attribute vec2 aCenter;
  attribute vec2 aTangent;
  attribute float aSide;
  attribute float aRank;
  attribute float aWidth;     // the stroke's thickness in px, from the tips it feeds

  uniform float uAspect;
  uniform float uViewportH;
  uniform float uReveal;      // rank threshold, walks the growth order
  uniform float uFade;        // 0..1 extinction progress
  uniform float uResidual;    // the afterimage: a floor under the halo, never the core
  uniform float uWidthScale;

  varying float vD;           // px from the axis, signed
  varying float vW;           // thickness, px
  varying float vA;           // the discharge's own envelope
  varying float vSafe;
  varying float vReached;

  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  float safeZoneAttenuation(vec2 ndc) {
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }

  void main() {
    // Not yet reached by the propagation: the vertex stays ON ITS AXIS with
    // nothing lit, so the quad that joins it to a reached one tapers to a
    // point - the growing tip. Sending it off-screen, as the first version
    // did, stretched that quad across the frame: a straight streak from the
    // front of the growth to the corner.
    float reached = step(aRank, uReveal);
    float w = aWidth * uWidthScale * reached;
    // Extinction runs from the tips inward: a thin branch starts going out
    // immediately, the trunk not until well into the fade.
    float tipN = 1.0 - clamp((aWidth - ${WIDTH_TIP_PX.toFixed(1)}) / ${(WIDTH_TRUNK_PX - WIDTH_TIP_PX).toFixed(1)}, 0.0, 1.0);
    float start = ${FADE_START_TRUNK.toFixed(2)} * (1.0 - tipN);
    float a = 1.0 - smoothstep(start, start + ${FADE_SPAN.toFixed(2)}, uFade);

    // The ribbon reaches the halo's edge, plus a pixel for the antialiasing.
    float halfPx = 0.5 * ${HALO_SPAN.toFixed(1)} * w + 1.0;
    // The perpendicular is taken in HEIGHT units, which are square in pixels,
    // and only then squeezed into NDC - otherwise a stroke is thicker on one
    // axis than on the other.
    vec2 t = normalize(aTangent + vec2(1e-7));
    vec2 perp = vec2(-t.y, t.x);
    vec2 off = perp * (2.0 * halfPx / uViewportH) * aSide;
    vec2 ndc = vec2((aCenter.x + off.x) / uAspect, aCenter.y + off.y);

    vD = aSide * halfPx;
    vW = max(w, 0.01);
    vA = a;
    vReached = reached;
    vSafe = safeZoneAttenuation(ndc);
    gl_Position = vec4(ndc, 0.0, 1.0);
  }
`;

// A pure white core 30% of the thickness inside a blue-violet halo four times
// the thickness. Every edge is antialiased in PIXELS: a 1.5 px tip has a
// 0.45 px core, which is drawn as coverage, not as a line that flickers in
// and out of existence as it crosses pixel centres.
const boltFragmentShader = `
  precision highp float;
  uniform vec3 uHalo;
  uniform float uCore;        // 1 for a live discharge, 0 for an afterimage
  uniform float uResidual;
  uniform float uAlpha;
  varying float vD;
  varying float vW;
  varying float vA;
  varying float vSafe;
  varying float vReached;
  void main() {
    float d = abs(vD);
    float body = clamp(0.5 * vW + 0.5 - d, 0.0, 1.0);
    float ch = 0.5 * ${CORE_SHARE.toFixed(2)} * vW;
    float core = clamp(ch + 0.5 - d, 0.0, 1.0) * min(1.0, 2.0 * ch + 0.35);
    float hx = d / (0.5 * ${HALO_SPAN.toFixed(1)} * vW);
    float glow = exp(-hx * hx * 3.0) * (1.0 - smoothstep(0.8, 1.0, hx));
    vec3 col = uHalo * (0.60 * body + 0.50 * glow) * max(vA, uResidual);
    col = mix(col, vec3(${PEAK_LUMINANCE.toFixed(2)}), core * vA * uCore);
    gl_FragColor = vec4(col * uAlpha * vSafe * vReached, 1.0);
  }
`;

const bgVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

// The storm sky, at FULL resolution, every gradient dithered. The first
// version's corona drew its noise on a 40x40 lattice of blocks and banded on
// top of that: the "pixelated violet glow" at the bottom of the frame.
const bgFragmentShader = `
  precision highp float;
  uniform float uAspect;
  uniform float uTime;
  uniform vec3 uHalo;
  uniform float uFlash;       // 1 for the two frames of the strike
  uniform float uCloudLight;  // the clouds lit from inside, 400 ms
  uniform vec2 uCloudPos;     // where, in height units
  uniform float uAirGlow;
  uniform vec2 uAirPos;
  uniform float uHorizonGlow;
  uniform sampler2D uClouds;
  varying vec2 vUv;

  float hash12(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }

  void main() {
    vec2 ndc = vUv * 2.0 - 1.0;
    vec2 p = vec2(ndc.x * uAspect, ndc.y);
    // Very dark, a touch lighter toward the horizon.
    float h = clamp((p.y - ${HORIZON_Y.toFixed(2)}) / ${(1 - HORIZON_Y).toFixed(2)}, 0.0, 1.0);
    vec3 col = mix(vec3(0.018, 0.017, 0.028), vec3(0.009, 0.009, 0.014), h);

    // CLOUDS, the upper third: dark masses, darker than the sky between strikes.
    vec4 c = texture2D(uClouds, vec2(p.x * 0.16 + uTime * ${CLOUD_DRIFT.toFixed(4)}, clamp((p.y - 0.15) / 0.85, 0.0, 1.0)));
    float base = 0.30 + 0.16 * (c.b - 0.5);
    float dens = c.r * smoothstep(base, base + 0.30, p.y);
    col = mix(col, vec3(0.004, 0.004, 0.007), dens * 0.8);
    col += vec3(0.006, 0.005, 0.010) * dens * c.g;          // their relief, barely

    // THE FLASH: the whole sky to 0.35 for two frames. Clouds stay a little
    // darker than the sky around them - that difference is their volume.
    col += vec3(0.33, 0.345, 0.43) * uFlash * (1.0 - 0.5 * dens);

    // The clouds lit FROM INSIDE around the leader's start.
    vec2 d = (p - uCloudPos) * vec2(0.75, 1.25);
    float L = uCloudLight * exp(-dot(d, d) * 2.6);
    col += (vec3(0.78, 0.76, 1.0) + uHalo * 0.4) * L * pow(dens, 0.7) * (0.35 + 0.65 * c.g) * 0.95;
    // ...and the air under them a little.
    col += (vec3(0.10, 0.10, 0.16) + uHalo * 0.12) * L * 0.35 * smoothstep(0.1, 0.6, p.y);

    // The air around the strike.
    vec2 a = (p - uAirPos) * vec2(0.8, 1.0);
    col += (uHalo * 0.22 + vec3(0.05, 0.055, 0.08)) * uAirGlow * exp(-dot(a, a) * 1.4);

    // The glow behind the horizon, which the silhouette cuts out.
    float above = max(p.y - ${HORIZON_Y.toFixed(2)}, 0.0);
    float g = exp(-above / 0.14);
    col += uHalo * g * (0.035 + 0.55 * uHorizonGlow) + vec3(0.05, 0.05, 0.07) * g * uHorizonGlow;

    // Triangular dither, one 255th: no gradient here may band.
    col += (hash12(gl_FragCoord.xy + fract(uTime * 7.0) * 91.0) + hash12(gl_FragCoord.yx * 1.37 + 17.0) - 1.0) / 255.0;
    gl_FragColor = vec4(max(col, 0.0), 1.0);
  }
`;

// BETWEEN STRIKES, each flash is its OWN quad over its own patch of sky, drawn
// only while it is lit. A loop over six flashes in the full-frame shader cost
// 15 ms on the Pi: its GPU runs every branch on every pixel of the frame.
const pulseVertexShader = `
  precision highp float;
  uniform float uAspect;
  uniform vec2 uCentre;       // height units
  uniform vec2 uHalf;
  varying vec2 vP;
  void main() {
    vP = uCentre + position.xy * 2.0 * uHalf;
    gl_Position = vec4(vP.x / uAspect, vP.y, 0.0, 1.0);
  }
`;
const pulseFragmentShader = `
  precision highp float;
  uniform float uTime;
  uniform vec3 uHalo;
  uniform vec2 uCentre;
  uniform float uLevel;
  uniform float uKind;        // 0: inside the clouds, 1: behind the horizon
  uniform sampler2D uClouds;
  varying vec2 vP;
  void main() {
    vec2 p = vP;
    vec3 col;
    if (uKind < 0.5) {
      // The same cloud deck as the sky shader: the flash lights ITS density.
      vec4 c = texture2D(uClouds, vec2(p.x * 0.16 + uTime * ${CLOUD_DRIFT.toFixed(4)}, clamp((p.y - 0.15) / 0.85, 0.0, 1.0)));
      float base = 0.30 + 0.16 * (c.b - 0.5);
      float dens = c.r * smoothstep(base, base + 0.30, p.y);
      vec2 e = (p - uCentre) * vec2(1.1, 1.8);
      float k = exp(-dot(e, e) * 4.0) * uLevel;
      col = (vec3(0.70, 0.68, 0.95) + uHalo * 0.45) * k * pow(dens, 0.8) * (0.35 + 0.65 * c.g) + uHalo * k * 0.05;
    } else {
      float above = max(p.y - ${HORIZON_Y.toFixed(2)}, 0.0);
      float dx = (p.x - uCentre.x) * 2.2;
      col = (uHalo * 0.6 + vec3(0.10, 0.10, 0.14)) * exp(-above / 0.14) * exp(-dx * dx) * uLevel;
    }
    gl_FragColor = vec4(col, 1.0);
  }
`;

// The horizon: hills and a stretch of town, black, in front of everything.
const silVertexShader = `
  precision highp float;
  uniform float uAspect;
  void main() { gl_Position = vec4(position.x / uAspect, position.y, 0.0, 1.0); }
`;
const silFragmentShader = `
  precision highp float;
  void main() { gl_FragColor = vec4(0.004, 0.004, 0.007, 1.0); }
`;

// Dust, visible only during the flash.
const dustVertexShader = `
  precision mediump float;
  attribute vec3 aSeed;     // x,y position in NDC, z size
  uniform float uGlow;
  uniform float uDprScale;
  varying float vA;
  void main() {
    vA = uGlow * (0.4 + 0.6 * fract(aSeed.z * 13.7));
    gl_Position = vec4(aSeed.xy, 0.0, 1.0);
    gl_PointSize = clamp(aSeed.z * 2.2 * uDprScale, 1.0, 4.0);
  }
`;
const dustFragmentShader = `
  precision mediump float;
  varying float vA;
  void main() {
    vec2 c = gl_PointCoord - 0.5;
    if (length(c) > 0.5) discard;
    gl_FragColor = vec4(vec3(0.60, 0.64, 0.82) * vA * 0.45, 1.0);
  }
`;

// A small deterministic generator, so the clouds and the skyline can be
// reproduced from a seed.
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// The cloud deck, baked once: fbm is five octaves of noise per pixel, which is
// no price to pay every frame on the Pi for something that moves at a
// millimetre a second. R: density, G: inner relief, B: where the base sits.
// Tiles in x, so it can scroll.
function makeCloudTexture(seed) {
  const W = 512, H = 128;
  const rnd = mulberry32(seed);
  const octaves = [];
  for (let o = 0; o < 6; o++) {
    const px = 4 << o, py = 2 << o;
    const v = new Float32Array(px * (py + 1));
    for (let i = 0; i < v.length; i++) v[i] = rnd();
    octaves.push({ px, py, v });
  }
  const noise = (oc, x, y) => {
    const fx = x * oc.px, fy = y * oc.py;
    const ix = Math.floor(fx), iy = Math.floor(fy);
    const tx = fx - ix, ty = fy - iy;
    const sx = tx * tx * (3 - 2 * tx), sy = ty * ty * (3 - 2 * ty);
    const at = (i, j) => oc.v[Math.min(oc.py, j) * oc.px + (((i % oc.px) + oc.px) % oc.px)];
    const a = at(ix, iy), b = at(ix + 1, iy), c = at(ix, iy + 1), d = at(ix + 1, iy + 1);
    return (a + (b - a) * sx) + ((c + (d - c) * sx) - (a + (b - a) * sx)) * sy;
  };
  const data = new Uint8Array(W * H * 4);
  const ss = (e0, e1, x) => { const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0))); return t * t * (3 - 2 * t); };
  for (let j = 0; j < H; j++) {
    for (let i = 0; i < W; i++) {
      const x = i / W, y = j / H;
      let f = 0, amp = 0.5, tot = 0;
      for (let o = 0; o < 5; o++) { f += noise(octaves[o], x, y) * amp; tot += amp; amp *= 0.5; }
      f /= tot;
      let g = 0; amp = 0.5; tot = 0;
      for (let o = 2; o < 6; o++) { g += noise(octaves[o], x + 0.37, y + 0.11) * amp; tot += amp; amp *= 0.5; }
      g /= tot;
      const k = (j * W + i) * 4;
      data[k] = Math.round(255 * ss(0.40, 0.70, f));
      data[k + 1] = Math.round(255 * ss(0.30, 0.75, g));
      data[k + 2] = Math.round(255 * noise(octaves[1], x, 0.5));
      data[k + 3] = 255;
    }
  }
  const tex = new THREE.DataTexture(data, W, H, THREE.RGBAFormat);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

// The skyline, in height units, sampled over x in [-SKY_X, SKY_X]: rolling
// hills, and on one side a stretch of town with a mast or two.
const SKY_X = 2.4;
const SKY_N = 1600;           // fine enough for square roofs: 480 rounded them
function makeSkyline(seed) {
  const rnd = mulberry32(seed);
  const ph = [rnd() * 6.28, rnd() * 6.28, rnd() * 6.28];
  const hs = new Float32Array(SKY_N + 1);
  const side = rnd() < 0.5 ? -1 : 1;
  const townA = side * (0.55 + rnd() * 0.35), townW = 0.55 + rnd() * 0.3;
  const blocks = [];
  let x = townA - townW / 2;
  while (x < townA + townW / 2) {
    const w = 0.035 + rnd() * 0.08;
    const edge = 1 - Math.abs(x + w / 2 - townA) / (townW / 2);
    blocks.push([x, x + w, HORIZON_Y + 0.02 + (0.03 + rnd() * 0.09) * (0.35 + 0.65 * edge)]);
    x += w + (rnd() < 0.2 ? 0.01 : 0);
  }
  const masts = [[townA + (rnd() - 0.5) * townW * 0.6, HORIZON_Y + 0.21 + rnd() * 0.05]];
  for (let i = 0; i <= SKY_N; i++) {
    const X = -SKY_X + (2 * SKY_X * i) / SKY_N;
    let y = HORIZON_Y - 0.025 + 0.030 * Math.sin(X * 1.7 + ph[0]) + 0.018 * Math.sin(X * 4.3 + ph[1])
          + 0.007 * Math.sin(X * 11.0 + ph[2]);
    for (const b of blocks) if (X >= b[0] && X < b[1]) y = Math.max(y, b[2]);
    for (const m of masts) if (Math.abs(X - m[0]) < 0.004) y = Math.max(y, m[1]);
    hs[i] = y;
  }
  return hs;
}

export class LichtenbergVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER[this._tier];

    const e = parseFloat(urlParam('bolteta'));
    this._forcedEta = Number.isFinite(e) ? e : null;
    const t = urlParam('bolttype');
    this._forcedType = TYPES[t] ? t : null;
    this._forceFire = urlParam('boltfire') === '1';
    // ?boltholdat=connect freezes the frame the bolt reaches the ground - the
    // flash - so it can be looked at. ?boltion=1 runs the pre-drop filaments
    // without a drop.
    // ?boltholdat=after freezes the frame just after the flash.
    this._holdAtConnect = urlParam('boltholdat') === 'connect';
    this._holdAfterFlash = urlParam('boltholdat') === 'after';
    this._forceIon = urlParam('boltion') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    camera.position.set(0, 0, 1);
    camera.lookAt(0, 0, 0);

    this._time = 0;
    this._paused = false;
    this._frozen = false;
    this._aspect = camera && camera.aspect ? camera.aspect : 1.78;
    this._viewportH = 1080;
    this._lastFireMs = -1e9;
    this._lastLow = 0;
    this._lastPaletteRef = undefined;
    this._halo = new THREE.Color(BLUE_VIOLET);
    this._builder = null;
    this._building = false;
    this._ready = null;
    this._idleHandle = null;
    this._miniHandle = null;
    this._synthNext = 2.5;
    this._nowMs = 0;
    this._flashFrames = 0;
    this._cloudLightAt = -1e9;
    this._airGlow = 0;
    this._ionActive = false;
    this._ionPlan = [];
    this._ionNextForced = 0;
    this._miniPool = [];
    this._distantPool = [];
    this._distantHandle = null;
    this._pulses = Array.from({ length: PULSE_SLOTS }, () => ({ t0: -1e9, x: 0, y: 0, level: 0, kind: 0, twin: false }));
    this._pulseNext = 0;
    this._lastBeat = null;
    this._lastBar = null;
    this._ownBeatMs = 0;
    this._ownBeats = 0;
    this._seed = (Math.random() * 1e9) | 0;
    this._skyline = makeSkyline(this._seed);

    this._maxSites = Math.round(1200 * this._layout.siteScale);

    this._buildBackground();
    this._buildPulses();
    this._buildSilhouette();
    this._buildStrokes();
    this._buildFilaments();
    this._buildDistant();
    if (this._layout.dust) this._buildDust();
    this._scheduleBuild();
    this._pumpMini();
    this._pumpDistant();
  }

  // --- scene ---------------------------------------------------------------

  _buildBackground() {
    this._cloudTex = makeCloudTexture(this._seed ^ 0x5bd1e995);
    this._bgUniforms = {
      uAspect: { value: this._aspect },
      uTime: { value: 0 },
      uHalo: { value: this._halo },
      uFlash: { value: 0 },
      uCloudLight: { value: 0 },
      uCloudPos: { value: new THREE.Vector2(0, 0.8) },
      uAirGlow: { value: 0 },
      uAirPos: { value: new THREE.Vector2(0, 0) },
      uHorizonGlow: { value: 0 },
      uClouds: { value: this._cloudTex }
    };
    this._bgGeo = new THREE.PlaneGeometry(1, 1);
    this._bgMat = new THREE.ShaderMaterial({
      uniforms: this._bgUniforms, vertexShader: bgVertexShader, fragmentShader: bgFragmentShader,
      depthTest: false, depthWrite: false
    });
    this._bg = new THREE.Mesh(this._bgGeo, this._bgMat);
    this._bg.frustumCulled = false;
    this._bg.renderOrder = -100;
    this._scene.add(this._bg);
  }

  _buildPulses() {
    this._pulseGeo = new THREE.PlaneGeometry(1, 1);
    this._pulseMeshes = [];
    for (let i = 0; i < PULSE_SLOTS; i++) {
      const u = {
        uAspect: { value: this._aspect }, uTime: { value: 0 }, uHalo: { value: this._halo },
        uCentre: { value: new THREE.Vector2() }, uHalf: { value: new THREE.Vector2(0.9, 0.55) },
        uLevel: { value: 0 }, uKind: { value: 0 }, uClouds: { value: this._cloudTex }
      };
      const mat = new THREE.ShaderMaterial({
        uniforms: u, vertexShader: pulseVertexShader, fragmentShader: pulseFragmentShader,
        transparent: true, depthTest: false, depthWrite: false, blending: THREE.AdditiveBlending
      });
      const m = new THREE.Mesh(this._pulseGeo, mat);
      m.frustumCulled = false;
      m.renderOrder = -90;
      // Visible on the first frame, at level 0, so the program compiles then
      // and not on the first beat.
      this._scene.add(m);
      this._pulseMeshes.push({ mesh: m, mat, u });
    }
  }

  // The horizon, in front of the strokes: a bolt that goes down behind it has
  // struck it.
  _buildSilhouette() {
    const hs = this._skyline;
    const pos = new Float32Array((SKY_N + 1) * 2 * 3);
    for (let i = 0; i <= SKY_N; i++) {
      const X = -SKY_X + (2 * SKY_X * i) / SKY_N;
      pos.set([X, -1.05, 0, X, hs[i], 0], i * 6);
    }
    const idx = [];
    for (let i = 0; i < SKY_N; i++) {
      const a = i * 2, b = a + 1, c = a + 2, d = a + 3;
      idx.push(a, b, d, a, d, c);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setIndex(idx);
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 10);
    this._silUniforms = { uAspect: { value: this._aspect } };
    this._silMat = new THREE.ShaderMaterial({
      uniforms: this._silUniforms, vertexShader: silVertexShader, fragmentShader: silFragmentShader,
      depthTest: false, depthWrite: false, side: THREE.DoubleSide
    });
    this._silGeo = geo;
    this._sil = new THREE.Mesh(geo, this._silMat);
    this._sil.frustumCulled = false;
    this._sil.renderOrder = 30;
    this._scene.add(this._sil);
  }

  _groundAt(x) {
    const f = (x + SKY_X) / (2 * SKY_X) * SKY_N;
    const i = Math.max(0, Math.min(SKY_N - 1, Math.floor(f)));
    const t = Math.max(0, Math.min(1, f - i));
    return this._skyline[i] + (this._skyline[i + 1] - this._skyline[i]) * t;
  }

  // One mesh per retained discharge plus the live one, each sized for the
  // tier's biggest figure and refilled in place: a strike allocates nothing.
  _buildStrokes() {
    this._strokes = [];
    const n = this._layout.retain + 1;
    for (let i = 0; i < n; i++) this._strokes.push(this._makeStroke(this._maxSites * 6, 10));
    this._live = this._strokes[0];
    this._history = [];
  }

  _buildFilaments() {
    this._filaments = [];
    for (let i = 0; i < FILAMENT_SLOTS; i++) {
      const f = this._makeStroke(FILAMENT_SITES[1] * 8, 12);
      f.uniforms.uWidthScale.value = 0.38;
      f.uniforms.uCore.value = 1;
      this._filaments.push(f);
    }
  }

  _buildDistant() {
    this._distant = [];
    for (let i = 0; i < DISTANT_SLOTS; i++) {
      const f = this._makeStroke(DISTANT_SITES * 7, 9);
      f.uniforms.uWidthScale.value = 0.42;
      f.uniforms.uCore.value = 1;
      this._distant.push(f);
    }
  }

  // Small bolts for the far horizon, grown in idle time like the filaments.
  _pumpDistant() {
    const self = this;
    const run = function () {
      self._distantHandle = null;
      if (self._distantPool.length >= DISTANT_POOL) return;
      const b = new DischargeBuilder('bolt', 2.2, DISTANT_SITES, null, 64);
      while (!b.step(60)) { /* a 64x64 grid */ }
      // Laid out here too, not on the bar it appears: the smoothing pipeline
      // cost 9 ms in the frame on the Pi.
      self._distantPool.push(self._layoutDistant(b.tree()));
      self._pumpDistant();
    };
    if (this._distantPool.length >= DISTANT_POOL || this._distantHandle != null) return;
    this._distantHandle = typeof requestIdleCallback === 'function'
      ? requestIdleCallback(run, { timeout: 300 }) : setTimeout(run, 0);
  }

  // One intra-cloud flash, somewhere in the deck.
  _pulse(level, kind, x, y) {
    let slot = this._pulses[0];
    for (const q of this._pulses) if (q.t0 < slot.t0) slot = q;
    const a = Math.max(0.5, this._aspect);
    slot.t0 = this._nowMs;
    slot.kind = kind;
    slot.x = x != null ? x : (Math.random() * 2 - 1) * 0.95 * a;
    slot.y = y != null ? y : lerp(0.48, 0.92, Math.random());
    slot.level = level;
    slot.twin = Math.random() < 0.4;   // some flicker twice
  }

  _layoutDistant(tree) {
    const a = Math.max(0.5, this._aspect);
    const side = Math.random() < 0.5 ? -1 : 1;
    const x = side * lerp(0.35, 0.92, Math.random()) * a;
    const N = tree.N, sites = tree.sites;
    let end = tree.impact;
    if (end < 0) { end = sites[0]; for (const s2 of sites) if (((s2 / N) | 0) > ((end / N) | 0)) end = s2; tree.impact = end; }
    const gy = Math.max(0.2, ((end / N) | 0) / (N - 1)), ge = (end % N) / (N - 1);
    const Hd = lerp(0.30, 0.55, Math.random());
    const ground = this._groundAt(x) - 0.01;
    const H = Hd / gy;
    const Xc = x - (ge - 0.5) * H;
    const paths = buildRenderPaths(tree,
      (gx, gyy) => [Xc + (gx / (N - 1) - 0.5) * H, ground + Hd - (gyy / (N - 1)) * H], { minTip: 1 });
    paths.x = x;
    return paths;
  }

  // A small bolt on the far horizon, lighting the skyline around it.
  _spawnDistant() {
    const paths = this._distantPool.pop();
    this._pumpDistant();
    if (!paths) return;
    const slot = this._distant.find((f) => this._nowMs - f.bornMs > DISTANT_MS);
    if (!slot) return;
    this._writeStroke(slot, paths);
    slot.bornMs = this._nowMs;
    slot.uniforms.uReveal.value = 1e9;
    this._pulse(0.9, 1, paths.x, HORIZON_Y);
    this._pulse(0.35, 0, paths.x, lerp(0.35, 0.55, Math.random()));
  }

  _makeStroke(capPoints, order) {
    const geo = new THREE.BufferGeometry();
    const verts = capPoints * 2;
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(verts * 3), 3));
    geo.setAttribute('aCenter', new THREE.BufferAttribute(new Float32Array(verts * 2), 2).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('aTangent', new THREE.BufferAttribute(new Float32Array(verts * 2), 2).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('aSide', new THREE.BufferAttribute(new Float32Array(verts), 1).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('aRank', new THREE.BufferAttribute(new Float32Array(verts), 1).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('aWidth', new THREE.BufferAttribute(new Float32Array(verts), 1).setUsage(THREE.DynamicDrawUsage));
    // The quads run BETWEEN consecutive points of a path and must not bridge
    // from the end of one path to the start of the next.
    geo.setIndex(new THREE.BufferAttribute(new Uint32Array(capPoints * 6), 1).setUsage(THREE.DynamicDrawUsage));
    geo.setDrawRange(0, 0);
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 10);

    const uniforms = {
      uAspect: { value: this._aspect },
      uViewportH: { value: 1080 },
      uReveal: { value: 0 },
      uFade: { value: 0 },
      uAlpha: { value: 1 },
      uResidual: { value: 0 },
      uCore: { value: 1 },
      uWidthScale: { value: 1 },
      uHalo: { value: new THREE.Color(BLUE_VIOLET) },
      uSafeAspect: safeZoneUniforms.uAspect,
      uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize,
      uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    const mat = new THREE.ShaderMaterial({
      uniforms: uniforms, vertexShader: boltVertexShader, fragmentShader: boltFragmentShader,
      transparent: true, depthTest: false, depthWrite: false, blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.frustumCulled = false;
    mesh.renderOrder = order;
    // ALWAYS visible, with an empty draw range when unused: three compiles a
    // program the first time an object is drawn, and a stroke first shown on
    // the first strike cost an 80-100 ms hitch on the Pi, at the very moment
    // the frame has to be perfect. With a zero draw range it compiles on the
    // first frame and draws nothing.
    this._scene.add(mesh);
    return { geo, mat, uniforms, mesh, capPoints, segs: null, bornMs: -1e9, growMs: 120, fadeMs: 500, total: 1 };
  }

  _buildDust() {
    const n = 220;
    const seed = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      seed[i * 3] = Math.random() * 2 - 1;
      seed[i * 3 + 1] = Math.random() * 1.8 - 0.8;
      seed[i * 3 + 2] = 0.4 + Math.random() * 1.2;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    geo.setAttribute('aSeed', new THREE.BufferAttribute(seed, 3));
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 10);
    this._dustUniforms = { uGlow: { value: 0 }, uDprScale: { value: 1 } };
    this._dustMat = new THREE.ShaderMaterial({
      uniforms: this._dustUniforms, vertexShader: dustVertexShader, fragmentShader: dustFragmentShader,
      transparent: true, depthTest: false, depthWrite: false, blending: THREE.AdditiveBlending
    });
    this._dustGeo = geo;
    this._dust = new THREE.Points(geo, this._dustMat);
    this._dust.frustumCulled = false;
    this._dust.renderOrder = 5;
    this._scene.add(this._dust);
  }

  // --- the deferred build --------------------------------------------------
  // requestIdleCallback's timeRemaining() reads 0 under a continuous rAF loop,
  // which starved an earlier visualizer's generator down to a handful of items
  // in six seconds. So the slice always does a GUARANTEED floor of work and the
  // callback is only used for the scheduling, never for the budget - with a
  // timeout so it fires even when the page never goes idle.
  // hint: the strength we EXPECT the next strike to have. The figure is built
  // ahead of time, so its type has to be chosen here.
  _scheduleBuild(hint) {
    if (this._building || this._ready) return;
    const type = this._forcedType || this._pickType(hint != null ? hint : 0.55);
    const cfg = TYPES[type];
    const eta = this._forcedEta != null ? this._forcedEta : cfg.eta;
    const sites = Math.max(80, Math.round(cfg.sites * this._layout.siteScale));
    // A bolt comes down through a square window of the frame's height, off to
    // one side of the cover; the safe rectangle is expressed in THAT window.
    let safe = null;
    if (type === 'bolt') {
      const a = Math.max(0.5, this._aspect);
      const side = Math.random() < 0.5 ? -1 : 1;
      this._boltXc = side * lerp(0.30, 0.62, Math.random()) * a;
      safe = this._safeRectWindow(this._boltXc, 1.02 - HORIZON_Y);
    } else if (type === 'fracture') {
      safe = this._safeRectGrid();
    }
    // The builder itself is made in the idle callback, not here: its six
    // 65536-cell arrays and the initial field cost 13 ms on the Pi, and this
    // runs on the strike's own frame.
    this._buildArgs = [type, eta, sites, safe];
    this._building = true;
    this._builderXc = this._boltXc;
    this._pump();
  }

  _pump() {
    const self = this;
    const run = function () {
      self._idleHandle = null;
      if (!self._building) return;
      if (!self._builder) {
        self._builder = new DischargeBuilder(...self._buildArgs);
        self._pump();
        return;
      }
      // A fixed floor, not a deadline-driven budget.
      const finished = self._builder.step(40);
      if (finished) {
        self._ready = self._layoutFigure(self._builder);
        self._ready.type = self._builder.type;
        self._readyType = self._builder.type;
        self._builder = null;
        self._building = false;
        return;
      }
      self._pump();
    };
    this._idleHandle = typeof requestIdleCallback === 'function'
      ? requestIdleCallback(run, { timeout: 60 }) : setTimeout(run, 0);
  }

  // The pre-drop filaments come from a pool of small figures grown in idle
  // time, one per callback, so a drop never waits on the CPU.
  _pumpMini() {
    const self = this;
    const run = function () {
      self._miniHandle = null;
      if (self._miniPool.length >= FILAMENT_POOL) return;
      const n = Math.round(lerp(FILAMENT_SITES[0], FILAMENT_SITES[1], Math.random()));
      const b = new DischargeBuilder('bloom', 1.7, n, null, FILAMENT_GRID, true);
      while (!b.step(40)) { /* a 40x40 grid: a few hundred microseconds */ }
      self._miniPool.push(b.tree());
      self._pumpMini();
    };
    if (this._miniPool.length >= FILAMENT_POOL || this._miniHandle != null) return;
    this._miniHandle = typeof requestIdleCallback === 'function'
      ? requestIdleCallback(run, { timeout: 200 }) : setTimeout(run, 0);
  }

  // Where each kind of figure goes, and how big: the DBM gives the SHAPE, and
  // this decides the scale - the first version's scaling about the origin
  // left most figures as a tuft in a corner.
  _layoutFigure(builder) {
    const tree = builder.tree();
    const N = tree.N, sites = tree.sites;
    const a = Math.max(0.5, this._aspect);
    if (builder.type === 'bolt') {
      // From the top of the frame to the ground, the whole height. The end is
      // the site that bridged the gap, or failing that the lowest one, and the
      // scale is solved so it lands ON the skyline under it.
      if (tree.impact < 0) {
        let lo = sites[0];
        for (const s of sites) if (((s / N) | 0) > ((lo / N) | 0)) lo = s;
        tree.impact = lo;
      }
      const ge = (tree.impact % N) / (N - 1), gy = ((tree.impact / N) | 0) / (N - 1);
      const go = (sites[0] % N) / (N - 1);
      // Placed AFTER growth: the DBM drifts sideways as it comes down, and a
      // window placed beforehand put a whole bolt outside the frame. The
      // trunk's midpoint goes where the build aimed it, then both ends are
      // pulled inside 90% of the half-width.
      const want = this._builderXc != null ? this._builderXc : 0.5 * a;
      let H = 1.02 - HORIZON_Y, Xc = want;
      for (let k = 0; k < 4; k++) {
        Xc = want - ((go + ge) / 2 - 0.5) * H;
        const lim = 0.9 * a;
        const xo = Xc + (go - 0.5) * H, xe = Xc + (ge - 0.5) * H;
        const hi = Math.max(xo, xe), lo = Math.min(xo, xe);
        if (hi > lim) Xc -= hi - lim;
        if (lo < -lim) Xc += -lim - lo;
        const G = this._groundAt(Xc + (ge - 0.5) * H) - 0.012;
        H = (1.02 - G) / Math.max(0.2, gy);
      }
      return buildRenderPaths(tree, (x, y) => [Xc + (x / (N - 1) - 0.5) * H, 1.02 - (y / (N - 1)) * H]);
    }
    if (builder.type === 'bloom') {
      // A central spread 45% of the frame's width, centred on the cover.
      let x0 = N, x1 = 0, y0 = N, y1 = 0;
      for (const s of sites) { const x = s % N, y = (s / N) | 0; x0 = Math.min(x0, x); x1 = Math.max(x1, x); y0 = Math.min(y0, y); y1 = Math.max(y1, y); }
      let k = BLOOM_SPAN * 2 * a / Math.max(1, x1 - x0);
      k = Math.min(k, 1.7 / Math.max(1, y1 - y0));
      const mx = (x0 + x1) / 2, my = (y0 + y1) / 2;
      // R19: centred on the centre of composition, not on the cover.
      const cy = safeZoneUniforms.uStageCenter.value.y;
      return buildRenderPaths(tree, (x, y) => [(x - mx) * k, cy - (y - my) * k]);
    }
    // Fracture: electrode to electrode, across the whole frame.
    return buildRenderPaths(tree, (x, y) => [(x / (N - 1) - 0.5) * 2 * a, -((y / (N - 1)) - 0.5) * 2]);
  }

  // R19: the forbidden rectangle is the centred cover block (stage), not
  // the cover's actual position.
  _safeRectGrid() {
    const c = safeZoneUniforms.uStageCenter.value;
    const h = safeZoneUniforms.uStageHalfSize.value;
    const a = Math.max(0.2, this._aspect);
    const toGx = (x) => (x / a * 0.5 + 0.5) * (GRID - 1);
    const toGy = (y) => (0.5 - y * 0.5) * (GRID - 1);
    return {
      cx: toGx(c.x), cy: toGy(c.y),
      hx: (h.x / a * 0.5) * (GRID - 1), hy: (h.y * 0.5) * (GRID - 1)
    };
  }

  // The same rectangle, in the grid of a bolt window centred on Xc, H high.
  _safeRectWindow(Xc, H) {
    const c = safeZoneUniforms.uStageCenter.value; // R19
    const h = safeZoneUniforms.uStageHalfSize.value;
    return {
      cx: ((c.x - Xc) / H + 0.5) * (GRID - 1), cy: ((1.02 - c.y) / H) * (GRID - 1),
      hx: (h.x / H) * (GRID - 1), hy: (h.y / H) * (GRID - 1)
    };
  }

  _pickType(strength) {
    if (strength >= 0.8) return 'fracture';
    if (strength >= 0.5) return 'bolt';
    return 'bloom';
  }

  // --- firing --------------------------------------------------------------

  _fire(strength) {
    if (!this._ready) return false;
    const segs = this._ready;
    this._ready = null;

    // The previous live stroke becomes the freshest memory.
    if (this._live.segs) this._history.unshift(this._live);
    while (this._history.length > this._layout.retain) {
      const old = this._history.pop();
      old.segs = null;
      old.geo.setDrawRange(0, 0);
    }
    const free = this._strokes.find((s) => s !== this._live && this._history.indexOf(s) < 0);
    this._live = free || this._strokes[this._strokes.length - 1];

    this._writeStroke(this._live, segs);
    this._live.bornMs = this._nowMs;
    this._live.growMs = lerp(GROW_MS_MIN, GROW_MS_MAX, Math.random());
    this._live.fadeMs = lerp(FADE_MS_MIN, FADE_MS_MAX, 1 - clamp01(strength));
    this._live.total = segs.total;
    this._live.connected = false;
    this._live.origin = [segs.originX, segs.originY];
    this._live.impact = segs.impactX != null ? [segs.impactX, segs.impactY] : [segs.originX, segs.originY];
    this._lastFireMs = this._nowMs;

    this._scheduleBuild(strength);   // the NEXT one starts building now
    return true;
  }

  // The moment the leader reaches the ground: the return stroke. That is when
  // the sky lights, not when the leader leaves the cloud.
  _connect(st) {
    st.connected = true;
    this._flashFrames = FLASH_FRAMES;
    this._cloudLightAt = this._nowMs;
    this._bgUniforms.uCloudPos.value.set(st.origin[0], Math.max(st.origin[1], 0.55));
    this._bgUniforms.uAirPos.value.set((st.origin[0] + st.impact[0]) / 2, (st.origin[1] + st.impact[1]) / 2);
    this._airGlow = 1;
    if (this._holdAtConnect) this._frozen = true;
    if (this._holdAfterFlash) this._freezeAfterFlash = true;
  }

  // Two vertices per path point (one to each side), quads between consecutive
  // points of the SAME path.
  _writeStroke(stroke, paths) {
    const g = stroke.geo;
    const C = g.attributes.aCenter.array, T = g.attributes.aTangent.array;
    const S = g.attributes.aSide.array, R = g.attributes.aRank.array, Wd = g.attributes.aWidth.array;
    const idx = g.index.array;
    let pts = 0, ii = 0;
    for (let pi = 0; pi < paths.pathStart.length; pi++) {
      const start = paths.pathStart[pi], len = paths.pathLen[pi];
      if (pts + len > stroke.capPoints) break;
      const base = pts * 2;
      for (let i = 0; i < len; i++) {
        const k = start + i;
        for (let side = 0; side < 2; side++) {
          const o = pts * 2 + side;
          C[o * 2] = paths.cx[k]; C[o * 2 + 1] = paths.cy[k];
          T[o * 2] = paths.tx[k]; T[o * 2 + 1] = paths.ty[k];
          S[o] = side === 0 ? -1 : 1;
          R[o] = paths.rank[k];
          Wd[o] = paths.width[k];
        }
        pts++;
      }
      for (let i = 0; i < len - 1; i++) {
        const a = base + i * 2, b = a + 1, c = a + 2, d = a + 3;
        idx[ii++] = a; idx[ii++] = b; idx[ii++] = d;
        idx[ii++] = a; idx[ii++] = d; idx[ii++] = c;
      }
    }
    // Only the part written: the buffers are sized for the biggest figure, and
    // re-sending all of them cost 4 ms on the Pi on the strike's frame.
    for (const n of ['aCenter', 'aTangent', 'aSide', 'aRank', 'aWidth']) {
      const at = g.attributes[n];
      at.clearUpdateRanges();
      at.addUpdateRange(0, Math.max(1, pts * 2 * at.itemSize));
      at.needsUpdate = true;
    }
    g.index.clearUpdateRanges();
    g.index.addUpdateRange(0, Math.max(1, ii));
    g.index.needsUpdate = true;
    g.setDrawRange(0, ii);
    stroke.segs = paths;
  }

  // One pre-drop filament: a small figure from the pool, somewhere in the sky
  // clear of the cover, lit for 150 ms.
  _spawnFilament() {
    const tree = this._miniPool.pop();
    this._pumpMini();
    if (!tree) return;
    const slot = this._filaments.find((f) => this._nowMs - f.bornMs > FILAMENT_LIFE_MS) || null;
    if (!slot) return;
    const a = Math.max(0.5, this._aspect);
    // R19: kept clear of the centre of composition, not of the cover.
    const c = safeZoneUniforms.uStageCenter.value, h = safeZoneUniforms.uStageHalfSize.value;
    let x = 0, y = 0;
    for (let k = 0; k < 20; k++) {
      x = (Math.random() * 2 - 1) * 0.92 * a;
      y = lerp(HORIZON_Y + 0.22, 0.88, Math.random());
      if (Math.abs(x - c.x) > h.x + 0.12 || Math.abs(y - c.y) > h.y + 0.12) break;
    }
    const N = tree.N;
    let r = 1;
    for (const s of tree.sites) r = Math.max(r, Math.hypot((s % N) - N / 2, ((s / N) | 0) - N / 2));
    const k = lerp(0.05, 0.10, Math.random()) / r;
    this._writeStroke(slot, buildRenderPaths(tree, (gx, gy) => [x + (gx - N / 2) * k, y - (gy - N / 2) * k], { minTip: 0 }));
    slot.bornMs = this._nowMs;
    slot.uniforms.uReveal.value = 1e9;
  }

  // --- frame ---------------------------------------------------------------

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    this._paused = paused;
    if (paused || this._frozen) return;   // gel: a discharge frozen mid-propagation

    const dt = Math.min(deltaTime, 0.05);
    this._time += dt;
    this._nowMs += dt * 1000;

    const cam = this._camera;
    this._aspect = cam && cam.aspect ? cam.aspect : 1.78;
    // The RENDER buffer, not the CSS height: stroke widths are specified in
    // pixels and the renderer runs at a fraction of screen resolution.
    this._viewportH = Math.max(1, getRenderBufferHeight());
    const director = profile.director;
    const bands = director && director.bands;
    const has = !!(bands && director.hasReliableGrid);

    // --- anticipation on a known drop --------------------------------------
    // nextEvent.msUntil is already milliseconds.
    const next = director && director.nextEvent;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 128;
    const beatMs = 60000 / bpm;
    const windowMs = ANTICIPATE_BEATS * beatMs;
    const inWindow = !!(next && next.score >= 0.6 && next.msUntil <= windowMs && next.msUntil > 0);

    // Without an analysis, bands are all zeros and no rise ever happens - so
    // the fallback has to generate its own events rather than watch for them.
    let low;
    if (has) {
      low = clamp01(bands.low);
    } else {
      this._synthNext -= dt;
      low = 0;
      if (this._synthNext <= 0) { this._synthNext = lerp(4.2, 7.0, Math.random()); low = 1; }
    }
    const lowPeak = low - this._lastLow > LOW_PEAK_RISE || (!has && low > 0.5);
    this._lastLow = low;

    // --- ionisation filaments: ONLY in the four beats before a known drop ---
    if (inWindow && !this._ionActive) {
      this._ionActive = true;
      const n = Math.round(lerp(FILAMENT_COUNT[0], FILAMENT_COUNT[1], Math.random()));
      this._ionPlan = [];
      for (let i = 0; i < n; i++) this._ionPlan.push(this._nowMs + Math.random() * Math.max(100, next.msUntil - 60));
      this._ionPlan.sort((p, q) => q - p);
    } else if (!inWindow) {
      this._ionActive = false;
      this._ionPlan.length = 0;
    }
    if (this._forceIon && this._nowMs > this._ionNextForced) {
      this._ionNextForced = this._nowMs + lerp(60, 320, Math.random());
      this._ionPlan.push(this._nowMs);
    }
    while (this._ionPlan.length && this._ionPlan[this._ionPlan.length - 1] <= this._nowMs) {
      this._ionPlan.pop();
      this._spawnFilament();
    }
    for (const f of this._filaments) {
      const age = this._nowMs - f.bornMs;
      if (!f.segs || age > FILAMENT_LIFE_MS) {
        if (f.segs) { f.segs = null; f.geo.setDrawRange(0, 0); }
        continue;
      }
      const t = age / FILAMENT_LIFE_MS;
      const u = f.uniforms;
      u.uAlpha.value = t < 0.15 ? t / 0.15 : 1 - (t - 0.15) / 0.85;
      u.uAspect.value = this._aspect;
      u.uViewportH.value = this._viewportH;
      u.uHalo.value.copy(this._halo);
    }

    // --- between strikes: on the beat ---------------------------------------
    // beatIndex/barIndex come from the shared grid; with no tempo at all the
    // storm keeps a beat of its own.
    let beat = profile.beatIndex, bar = profile.barIndex;
    if (beat == null) {
      this._ownBeatMs += dt * 1000;
      const per = 60000 / FALLBACK_BPM;
      while (this._ownBeatMs >= per) { this._ownBeatMs -= per; this._ownBeats++; }
      beat = this._ownBeats; bar = Math.floor(this._ownBeats / 4);
    }
    const newBeat = this._lastBeat != null && beat !== this._lastBeat;
    const newBar = this._lastBar != null && bar !== this._lastBar;
    this._lastBeat = beat; this._lastBar = bar;
    const quiet = this._nowMs - this._lastFireMs < QUIET_AFTER_STRIKE_MS;
    if (newBeat && !quiet) {
      const drive = has ? lerp(0.6, 1.25, clamp01(bands.low)) : 1;
      const lvl = lerp(PULSE_LEVEL[0], PULSE_LEVEL[1], Math.random()) * drive;
      if (newBar) this._pulse(Math.min(1, lvl * PULSE_DOWNBEAT), 0);
      else if (Math.random() < PULSE_CHANCE) this._pulse(lvl, 0);
      if (newBar && ((bar % DISTANT_EVERY_BARS) === 0) && Math.random() < DISTANT_CHANCE) this._spawnDistant();
    }
    for (let i = 0; i < PULSE_SLOTS; i++) {
      const q = this._pulses[i], t = this._nowMs - q.t0;
      let e = t >= 0 ? Math.exp(-t / (PULSE_MS * 0.45)) : 0;
      if (q.twin && t > 70) e += 0.6 * Math.exp(-(t - 70) / (PULSE_MS * 0.35));
      if (t > PULSE_MS * 2.5) e = 0;
      const pm = this._pulseMeshes[i], lvl = e * q.level;
      pm.mesh.visible = lvl > 0.004 || this._nowMs < 100;
      pm.u.uLevel.value = lvl;
      pm.u.uKind.value = q.kind;
      pm.u.uAspect.value = this._aspect;
      pm.u.uTime.value = this._time;
      if (q.kind === 0) { pm.u.uCentre.value.set(q.x, q.y); pm.u.uHalf.value.set(0.97, 0.60); }
      else { pm.u.uCentre.value.set(q.x, HORIZON_Y + 0.30); pm.u.uHalf.value.set(1.05, 0.32); }
    }
    for (const f of this._distant) {
      const age = this._nowMs - f.bornMs;
      if (!f.segs) continue;
      if (age > DISTANT_MS) { f.segs = null; f.geo.setDrawRange(0, 0); continue; }
      // on, a dip, on again, out: how a far bolt reads
      const t = age / DISTANT_MS;
      const env = t < 0.14 ? 1 : t < 0.28 ? 0.3 : t < 0.46 ? 0.85 : 1 - (t - 0.46) / 0.54;
      const u = f.uniforms;
      u.uAlpha.value = DISTANT_LEVEL * Math.max(0, env);
      u.uAspect.value = this._aspect;
      u.uViewportH.value = this._viewportH;
      u.uHalo.value.copy(this._halo);
    }

    // --- firing -------------------------------------------------------------
    const sinceFire = this._nowMs - this._lastFireMs;
    let fire = false, strength = 0.5;
    if (this._forceFire && sinceFire > 1500) { fire = true; strength = 0.6; }
    else if (next && next.msUntil <= 16 && next.score >= 0.6) { fire = true; strength = next.score; }
    else if (lowPeak && sinceFire > MIN_GAP_MS) { fire = true; strength = 0.35 + 0.3 * low; }
    if (fire) this._fire(strength);
    // Keep the pre-built figure's morphology in step with what is coming.
    if (!this._building && !this._ready) this._scheduleBuild(next ? next.score : null);

    // --- the live discharge and its three afterimages -----------------------
    // The ghosts: the most recent finished discharge at 15%, then 8%, then 4%,
    // each gone 3 s after its own strike ended.
    const liveDone = this._live.segs && (this._nowMs - this._live.bornMs) > this._live.growMs + HOLD_MS + this._live.fadeMs;
    const ghosts = (liveDone ? [this._live] : []).concat(this._history);
    const all = [this._live].concat(this._history);
    for (let i = 0; i < all.length; i++) {
      const st = all[i];
      if (!st.segs) continue;
      const age = this._nowMs - st.bornMs;
      const u = st.uniforms;
      u.uAspect.value = this._aspect;
      u.uViewportH.value = this._viewportH;
      u.uHalo.value.copy(this._halo);
      u.uReveal.value = st.total * clamp01(age / st.growMs);
      if (st === this._live && !st.connected && age >= st.growMs) this._connect(st);
      const fadeAge = age - st.growMs - HOLD_MS;
      u.uFade.value = fadeAge <= 0 ? 0 : clamp01(fadeAge / st.fadeMs);
      u.uCore.value = st === this._live ? 1 : 0;
      const gi = ghosts.indexOf(st);
      if (gi < 0 && st === this._live) {
        // Still striking: the ghost level fades IN under the envelope, so the
        // trace is continuous when the envelope goes out.
        u.uResidual.value = AFTERGLOW_ALPHA[0] * clamp01(fadeAge / st.fadeMs);
      } else {
        const since = age - st.growMs - HOLD_MS - st.fadeMs;
        const life = clamp01(1 - since / RESIDUAL_MS);
        u.uResidual.value = (AFTERGLOW_ALPHA[gi] || 0) * life * life;
        if (st !== this._live) u.uFade.value = 1;
      }
    }

    // --- the sky -------------------------------------------------------------
    const flash = this._flashFrames > 0 ? 1 : 0;
    if (this._flashFrames > 0 && !this._frozen) this._flashFrames--;
    else if (this._freezeAfterFlash && flash === 0) this._frozen = true;
    const cl = clamp01(1 - (this._nowMs - this._cloudLightAt) / CLOUD_LIGHT_MS);
    this._airGlow = Math.max(0, this._airGlow - dt * 2.6);
    const bu = this._bgUniforms;
    bu.uFlash.value = flash * (SKY_FLASH / 0.35);
    bu.uCloudLight.value = cl * cl;
    bu.uAirGlow.value = this._layout.airGlow ? this._airGlow * 0.6 : 0;
    bu.uHorizonGlow.value = Math.max(flash, cl * 0.7);
    bu.uTime.value = this._time;
    bu.uAspect.value = this._aspect;
    this._silUniforms.uAspect.value = this._aspect;
    if (this._dustUniforms) {
      this._dustUniforms.uGlow.value = flash;
      this._dustUniforms.uDprScale.value = Math.min(window.devicePixelRatio || 1, 2);
    }

    // palette -> the HALO only, pulled toward blue-violet. A pink bolt does not
    // read as a bolt, so the constraint is absolute rather than a preference.
    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      const pal = profile.artworkPalette;
      const vivid = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors[0] : BLUE_VIOLET;
      this._halo.set(vivid).lerp(new THREE.Color(BLUE_VIOLET), BLUE_PULL);
    }
  }

  // Probe for the verification: the live figure's geometry, in height units.
  get probe() {
    const st = this._live;
    return st && st.segs ? {
      type: st.segs.type, points: st.segs.count, paths: st.segs.pathStart.length,
      origin: st.origin, impact: st.impact, groundAtImpact: st.impact ? this._groundAt(st.impact[0]) : null,
      segs: st.segs
    } : null;
  }

  dispose() {
    for (const h of [this._idleHandle, this._miniHandle, this._distantHandle]) {
      if (h == null) continue;
      if (typeof cancelIdleCallback === 'function') cancelIdleCallback(h);
      else clearTimeout(h);
    }
    this._idleHandle = this._miniHandle = this._distantHandle = null;
    this._distantPool.length = 0;
    this._builder = null;
    this._building = false;
    this._ready = null;
    this._miniPool.length = 0;
    for (const st of this._strokes.concat(this._filaments, this._distant)) {
      this._scene.remove(st.mesh);
      st.geo.dispose();
      st.mat.dispose();
    }
    this._scene.remove(this._bg);
    this._bgGeo.dispose();
    this._bgMat.dispose();
    this._cloudTex.dispose();
    for (const pm of this._pulseMeshes) { this._scene.remove(pm.mesh); pm.mat.dispose(); }
    this._pulseGeo.dispose();
    this._scene.remove(this._sil);
    this._silGeo.dispose();
    this._silMat.dispose();
    if (this._dust) {
      this._scene.remove(this._dust);
      this._dustGeo.dispose();
      this._dustMat.dispose();
    }
  }
}
